import { useState, useEffect, useRef } from 'react';
import { maps as mapsAPI, stroot } from '@birdi/js-sdk';
import type { Map } from '@birdi/js-sdk/src/public/maps';

/**
 * Custom hook to fetch map file count and storage asynchronously
 * @param maps - Array of maps or a function that returns maps
 * @returns Object with mapFiles state and a function to merge files info into maps
 */
export function useMapFiles(
  maps: Map[] | (() => Map[] | undefined),
): {
  mapFiles: Record<string, { fileCount: number; totalGB: number }>;
  getMapsWithFiles: (mapsToMerge: Map[]) => Map[];
} {
  const [mapFiles, setMapFiles] = useState<
    Record<string, { fileCount: number; totalGB: number }>
  >({});
  const requestedFilesRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const mapsArray = typeof maps === 'function' ? maps() : maps;
    if (!mapsArray || mapsArray.length === 0) return;

    // Filter out undefined/null maps
    const validMaps = mapsArray.filter((map): map is Map => !!map);
    if (validMaps.length === 0) return;

    const mapIds = validMaps
      .map((map) => map.id)
      .filter((id: string) => {
        // Only fetch files for maps we haven't requested yet
        return id && !requestedFilesRef.current.has(id);
      });

    if (mapIds.length === 0) {
      // No new maps to fetch
      return;
    }

    // Mark these as requested
    mapIds.forEach((id) => requestedFilesRef.current.add(id));

    // Fetch files in small batches (2 at a time) in parallel for faster loading
    // All batches start immediately, and files info appears as each batch completes
    const BATCH_SIZE = 2;
    const batches: string[][] = [];
    for (let i = 0; i < mapIds.length; i += BATCH_SIZE) {
      batches.push(mapIds.slice(i, i + BATCH_SIZE));
    }

    // Fetch all batches in parallel - each updates state as it completes
    batches.forEach((batch) => {
      mapsAPI
        .getMapFiles(stroot('gmpfls'), batch)
        .then((res) => {
          if (res.body) {
            // Update state immediately as each batch completes
            setMapFiles((prev) => ({ ...prev, ...res.body }));
          }
        })
        .catch((error) => {
          // Silently fail - files info is not critical, don't block other batches
          console.error(`Failed to fetch files for batch:`, error);
        });
    });
  }, [maps]);

  const getMapsWithFiles = (mapsToMerge: Map[]): Map[] => {
    return mapsToMerge.map((map) => {
      const filesInfo = mapFiles[map.id];
      return {
        ...map,
        // Keep as numbers (API returns numbers, components expect numbers)
        fileCount: filesInfo?.fileCount ?? map.fileCount ?? undefined,
        totalGB: filesInfo?.totalGB ?? map.totalGB ?? undefined,
      };
    });
  };

  return { mapFiles, getMapsWithFiles };
}

